import React, { useState, useEffect } from 'react';
import { trpc } from '@/lib/trpc';
import { Exercise, WorkoutSession, PlannedExercise, WorkoutSet, SetType } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, Save, Dumbbell, Play, RefreshCw, Link, Link2Off, TrendingUp, Zap, BarChart2, Battery, ChevronDown, ChevronUp, AlertTriangle, ArrowRight, X, CheckCircle2, RotateCcw } from 'lucide-react';
import { TrackingMode } from '@/lib/types';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ALL_SET_TYPES, SetTypeValue, getSetTypeBadgeConfig } from './SetTypeBadge';
import ExerciseLibrary from './ExerciseLibrary';
import { toast } from 'sonner';
import { PlateSelector } from './PlateSelector';
import { VoiceMicButton, VoiceResult } from './VoiceMicButton';
import { VoiceWorkoutPreviewCard, VoiceParsedWorkout, VoiceParsedExercise } from './VoiceWorkoutPreviewCard';
import { ForgePill, ForgeSectionHeader, ForgeColor } from '@/components/ui/ForgeUI';

interface WorkoutBuilderProps {
  selectedDate: string; // YYYY-MM-DD
  onSave: () => void;
  onStartLive: (session: WorkoutSession) => void;
  forceNewSession?: boolean; // When true, create a fresh draft instead of loading the existing session
  onForceNewSessionConsumed?: () => void; // Called after the new draft is created
}

export default function WorkoutBuilder({ selectedDate, onSave, onStartLive, forceNewSession, onForceNewSessionConsumed }: WorkoutBuilderProps) {
  const [session, setSession] = useState<WorkoutSession | null>(null);
  // Voice planner state
  const { data: userProfile } = trpc.profile.get.useQuery();
  const voiceEnabled = !!(userProfile as any)?.voiceLoggingEnabled;
  const [voicePreview, setVoicePreview] = useState<VoiceParsedWorkout | null>(null);
  const [showLibrary, setShowLibrary] = useState(false);
  const [splitCategory, setSplitCategory] = useState<string>('');

  const utils = trpc.useUtils();
  const { data: exercises = [] } = trpc.exercises.list.useQuery();
  const { data: splits = [] } = trpc.splits.list.useQuery();
  const { data: sessionsByDate } = trpc.sessions.byDate.useQuery({ date: selectedDate });
  const { data: allSessions = [] } = trpc.sessions.list.useQuery();
  const { data: plannerData } = trpc.sessions.getPlannerData.useQuery({ date: selectedDate });

  // Swap modal state
  const [swapModal, setSwapModal] = useState<{ peId: string; currentExercise: Exercise } | null>(null);
  // Set type picker state: peId -> setId -> open
  const [setTypePicker, setSetTypePicker] = useState<{ peId: string; setId: string } | null>(null);

  const saveSessionMutation = trpc.sessions.save.useMutation({
    onSuccess: () => {
      utils.sessions.list.invalidate();
      utils.sessions.byDate.invalidate();
    },
  });

  const saveTemplateMutation = trpc.templates.save.useMutation({
    onSuccess: () => {
      utils.templates.list.invalidate();
    },
  });

  // Determine split category for selected date
  useEffect(() => {
    const activeSplit = splits.find((s) => s.isActive);
    if (activeSplit) {
      const dateObj = new Date(selectedDate + 'T00:00:00');
      const dayOfWeek = dateObj.getDay();
      const splitDayIdx = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const splitDay = (activeSplit.days as any[])[splitDayIdx];
      setSplitCategory(splitDay?.category || '');
    }
  }, [splits, selectedDate]);

  // Load or create session for selected date
  useEffect(() => {
    if (sessionsByDate !== undefined) {
      if (forceNewSession) {
        // User explicitly chose "Add Another Workout" — create a fresh draft
        setSession({
          id: `session-${Date.now()}`,
          date: selectedDate,
          name: 'Daily Workout',
          plannedExercises: [],
        });
        onForceNewSessionConsumed?.();
      } else {
        const existing = sessionsByDate.find((s) => !s.completedAt && !s.isLive);
        if (existing) {
          setSession({
            ...existing,
            notes: existing.notes ?? undefined,
            completedAt: existing.completedAt ?? undefined,
            plannedExercises: existing.plannedExercises as PlannedExercise[],
          });
        } else {
          setSession({
            id: `session-${Date.now()}`,
            date: selectedDate,
            name: 'Daily Workout',
            plannedExercises: [],
          });
        }
      }
    }
  }, [sessionsByDate, selectedDate, forceNewSession]);

  if (!session) return null;

  const handleToggleSuperset = (peId: string, targetPeId: string | null) => {
    // Update the link
    const linked = session.plannedExercises.map(pe => {
      if (pe.id !== peId) return pe;
      return { ...pe, supersetWithId: targetPeId ?? undefined };
    });

    if (targetPeId) {
      // Reorder so the target is immediately after the source for a clean visual pairing
      const sourceIdx = linked.findIndex(pe => pe.id === peId);
      const targetIdx = linked.findIndex(pe => pe.id === targetPeId);
      if (sourceIdx !== -1 && targetIdx !== -1 && targetIdx !== sourceIdx + 1) {
        const reordered = [...linked];
        const [targetItem] = reordered.splice(targetIdx, 1);
        // Insert right after source (accounting for splice shift)
        const insertAt = targetIdx > sourceIdx ? sourceIdx + 1 : sourceIdx;
        reordered.splice(insertAt, 0, targetItem!);
        setSession({ ...session, plannedExercises: reordered });
        return;
      }
    }

    setSession({ ...session, plannedExercises: linked });
  };

  const handleAddExercise = (exercise: Exercise) => {
    if (session.plannedExercises.some((pe) => pe.exerciseId === exercise.id)) {
      toast.warning(`"${exercise.name}" is already in today's plan`);
      return;
    }
    const defaultSet: WorkoutSet = {
      id: `set-${Date.now()}-0`,
      reps: undefined,
      weight: undefined,
      completed: false,
    };
    const newPlanned: PlannedExercise = {
      id: `pe-${Date.now()}`,
      exerciseId: exercise.id,
      sets: [defaultSet],
    };
    setSession({ ...session, plannedExercises: [...session.plannedExercises, newPlanned] });
    toast.success(`Added "${exercise.name}" to plan`);
  };

  const handleRemoveExercise = (peId: string) => {
    setSession({ ...session, plannedExercises: session.plannedExercises.filter((pe) => pe.id !== peId) });
  };

  const handleSwapExercise = (peId: string, currentExercise: Exercise) => {
    setSwapModal({ peId, currentExercise });
  };

  const handleConfirmSwap = (peId: string, targetExercise: Exercise) => {
    if (session.plannedExercises.some((pe) => pe.exerciseId === targetExercise.id)) {
      toast.warning(`"${targetExercise.name}" is already in today's plan`);
      return;
    }
    setSession({
      ...session,
      plannedExercises: session.plannedExercises.map((pe) =>
        pe.id !== peId ? pe : { ...pe, exerciseId: targetExercise.id }
      ),
    });
    setSwapModal(null);
    toast.success(`Swapped to "${targetExercise.name}"`);
  };

  const handleGenerateWarmupSets = (peId: string) => {
    setSession({
      ...session,
      plannedExercises: session.plannedExercises.map((pe) => {
        if (pe.id !== peId) return pe;
        const workingSet = pe.sets.find((s) => s.weight !== undefined && s.weight > 0) || pe.sets[0];
        const targetWeight = workingSet?.weight || 135;
        const roundToGymWeight = (weight: number) => Math.round(weight / 2.5) * 2.5;
        const warmupSet1: WorkoutSet = {
          id: `set-warmup-1-${Date.now()}`,
          reps: 10,
          weight: roundToGymWeight(targetWeight * 0.5),
          completed: false,
          isWarmup: true,
        };
        const warmupSet2: WorkoutSet = {
          id: `set-warmup-2-${Date.now()}`,
          reps: 5,
          weight: roundToGymWeight(targetWeight * 0.75),
          completed: false,
          isWarmup: true,
        };
        const workingSets = pe.sets.filter((s) => !s.isWarmup);
        toast.success(`Generated 2 warm-up sets based on target of ${targetWeight} lbs!`);
        return { ...pe, sets: [warmupSet1, warmupSet2, ...workingSets] };
      }),
    });
  };

  const handleAddSet = (peId: string, type: SetType = 'straight') => {
    setSession({
      ...session,
      plannedExercises: session.plannedExercises.map((pe) => {
        if (pe.id !== peId) return pe;

        const ex = exercises.find(e => e.id === pe.exerciseId);
        const isTime = ex?.trackingMode === 'time';
        const nonChildSets = pe.sets.filter(s => !s.isChildBlock);
        const lastWorkingSet = [...nonChildSets].reverse().find(s =>
          s.setType !== 'warmup' && !s.isWarmup && s.setType !== 'feeder'
        ) ?? nonChildSets[nonChildSets.length - 1];
        const baseW = lastWorkingSet?.weight;
        const baseR = lastWorkingSet?.reps ?? 8;
        const baseDur = lastWorkingSet?.durationSeconds ?? 60;
        const ts = Date.now();
        const snap = (w: number) => Math.round(w / 2.5) * 2.5;

        // ── FEEDER: 2 warm-up rows BEFORE working sets ──────────────────────
        if (type === 'feeder') {
          const feeder1: WorkoutSet = {
            id: `set-${ts}-f1`, completed: false, setType: 'feeder',
            childLabel: 'F1', countsAsVolume: false,
            weight: baseW ? snap(baseW * 0.4) : undefined,
            reps: isTime ? undefined : 15,
            durationSeconds: isTime ? baseDur : undefined,
          };
          const feeder2: WorkoutSet = {
            id: `set-${ts + 1}-f2`, completed: false, setType: 'feeder',
            childLabel: 'F2', countsAsVolume: false,
            weight: baseW ? snap(baseW * 0.6) : undefined,
            reps: isTime ? undefined : 10,
            durationSeconds: isTime ? baseDur : undefined,
          };
          const feeders = pe.sets.filter(s => s.setType === 'feeder' || s.setType === 'warmup' || s.isWarmup);
          const rest = pe.sets.filter(s => s.setType !== 'feeder' && s.setType !== 'warmup' && !s.isWarmup);
          return { ...pe, sets: [...feeders, feeder1, feeder2, ...rest] };
        }

        // ── BACK-OFF: 1 row AFTER working sets at 65% ──────────────────────
        if (type === 'back_off') {
          const backOff: WorkoutSet = {
            id: `set-${ts}-bo`, completed: false, setType: 'back_off',
            childLabel: 'B', countsAsVolume: true,
            weight: baseW ? snap(baseW * 0.65) : undefined,
            reps: isTime ? undefined : (baseR ? baseR + 4 : 12),
            durationSeconds: isTime ? baseDur : undefined,
          };
          return { ...pe, sets: [...pe.sets, backOff] };
        }

        // ── DROP: parent + D1 (80%) + D2 (60%) ─────────────────────────────
        if ((type as string) === 'drop' || type === 'drop_set') {
          const parentId = `set-${ts}-drop`;
          const parent: WorkoutSet = {
            id: parentId, completed: false, setType: 'drop',
            weight: baseW, reps: isTime ? undefined : baseR,
            durationSeconds: isTime ? baseDur : undefined,
          };
          const child1: WorkoutSet = {
            id: `set-${ts + 1}-d1`, completed: false, setType: 'drop',
            parentSetId: parentId, isChildBlock: true, childLabel: 'D1',
            weight: baseW ? snap(baseW * 0.8) : undefined,
            reps: isTime ? undefined : (baseR ? baseR + 2 : 10),
            durationSeconds: isTime ? baseDur : undefined,
          };
          const child2: WorkoutSet = {
            id: `set-${ts + 2}-d2`, completed: false, setType: 'drop',
            parentSetId: parentId, isChildBlock: true, childLabel: 'D2',
            weight: baseW ? snap(baseW * 0.6) : undefined,
            reps: isTime ? undefined : (baseR ? baseR + 4 : 12),
            durationSeconds: isTime ? baseDur : undefined,
          };
          return { ...pe, sets: [...pe.sets, parent, child1, child2] };
        }

        // ── CLUSTER: parent + C1 + C2, same weight, 15s micro-rest ─────────
        if ((type as string) === 'cluster') {
          const parentId = `set-${ts}-cluster`;
          const clusterReps = Math.max(3, Math.round((baseR ?? 5) * 0.6));
          const parent: WorkoutSet = {
            id: parentId, completed: false, setType: 'cluster',
            weight: baseW, reps: isTime ? undefined : clusterReps,
            durationSeconds: isTime ? baseDur : undefined,
            microRestSeconds: 15,
          };
          const child1: WorkoutSet = {
            id: `set-${ts + 1}-c1`, completed: false, setType: 'cluster',
            parentSetId: parentId, isChildBlock: true, childLabel: 'C1',
            weight: baseW, reps: isTime ? undefined : clusterReps,
            durationSeconds: isTime ? baseDur : undefined,
            microRestSeconds: 15,
          };
          const child2: WorkoutSet = {
            id: `set-${ts + 2}-c2`, completed: false, setType: 'cluster',
            parentSetId: parentId, isChildBlock: true, childLabel: 'C2',
            weight: baseW, reps: isTime ? undefined : clusterReps,
            durationSeconds: isTime ? baseDur : undefined,
          };
          return { ...pe, sets: [...pe.sets, parent, child1, child2] };
        }

        // ── MECHANICAL DROP: parent + M1 + M2, same weight, variation note ─
        if ((type as string) === 'mechanical' || (type as string) === 'mechanical_drop') {
          const parentId = `set-${ts}-mech`;
          const parent: WorkoutSet = {
            id: parentId, completed: false, setType: 'mechanical',
            weight: baseW, reps: isTime ? undefined : baseR,
            durationSeconds: isTime ? baseDur : undefined,
            mechVariation: 'Variation 1',
          };
          const child1: WorkoutSet = {
            id: `set-${ts + 1}-m1`, completed: false, setType: 'mechanical',
            parentSetId: parentId, isChildBlock: true, childLabel: 'M1',
            weight: baseW, reps: isTime ? undefined : baseR,
            durationSeconds: isTime ? baseDur : undefined,
            mechVariation: 'Variation 2',
          };
          const child2: WorkoutSet = {
            id: `set-${ts + 2}-m2`, completed: false, setType: 'mechanical',
            parentSetId: parentId, isChildBlock: true, childLabel: 'M2',
            weight: baseW, reps: isTime ? undefined : baseR,
            durationSeconds: isTime ? baseDur : undefined,
            mechVariation: 'Variation 3',
          };
          return { ...pe, sets: [...pe.sets, parent, child1, child2] };
        }

        // ── All other types: single row ─────────────────────────────────────
        const newSet: WorkoutSet = {
          id: `set-${ts}-${pe.sets.length}`,
          reps: isTime ? undefined : baseR,
          weight: baseW,
          durationSeconds: isTime ? baseDur : undefined,
          completed: false,
          setType: type,
        };
        return { ...pe, sets: [...pe.sets, newSet] };
      }),
    });
  };

  const handleRevertSetToStraight = (peId: string, setId: string) => {
    setSession({
      ...session,
      plannedExercises: session.plannedExercises.map((pe) => {
        if (pe.id !== peId) return pe;
        const set = pe.sets.find(s => s.id === setId);
        if (!set) return pe;
        // Remove all children of this parent
        const filtered = pe.sets.filter(s => s.parentSetId !== setId);
        // Reset the parent to straight
        return {
          ...pe,
          sets: filtered.map(s =>
            s.id === setId ? { ...s, setType: 'straight' as SetType, microRestSeconds: undefined, mechVariation: undefined } : s
          ),
        };
      }),
    });
  };

  const handleRemoveSet = (peId: string, setId: string) => {
    setSession({
      ...session,
      plannedExercises: session.plannedExercises.map((pe) => {
        if (pe.id !== peId) return pe;
        if (pe.sets.length <= 1) return pe;
        return { ...pe, sets: pe.sets.filter((s) => s.id !== setId) };
      }),
    });
  };

  const handleSetChange = (peId: string, setId: string, field: keyof WorkoutSet, value: any) => {
    setSession({
      ...session,
      plannedExercises: session.plannedExercises.map((pe) => {
        if (pe.id !== peId) return pe;
        return {
          ...pe,
          sets: pe.sets.map((s) => (s.id !== setId ? s : { ...s, [field]: value })),
        };
      }),
    });
  };

  const handleSave = async () => {
    if (session.plannedExercises.length === 0) {
      toast.error('Please add at least one exercise to save the plan');
      return;
    }
    await saveSessionMutation.mutateAsync(session);
    toast.success('Workout plan saved successfully');
    onSave();
  };

  const handleLoadTemplate = async () => {
    const templates = await utils.templates.list.fetch();
    if (!templates || templates.length === 0) {
      toast.info('No saved templates found. Build a plan first and save it as a template!');
      return;
    }
    const categories = ['Push', 'Pull', 'Legs', 'Core/Cardio', 'Other'];
    let menuText = 'Choose a template to load:\n\n';
    categories.forEach((cat) => {
      const catTemplates = templates.filter((t) => (t.category || 'Other') === cat);
      if (catTemplates.length > 0) {
        menuText += `[${cat.toUpperCase()}]\n`;
        catTemplates.forEach((t) => {
          menuText += `  - ${t.name}\n`;
        });
        menuText += '\n';
      }
    });
    menuText += 'Enter template name exactly:';
    const templateId = prompt(menuText);
    if (templateId) {
      const matched = templates.find((t) => t.name.toLowerCase() === templateId.toLowerCase());
      if (matched) {
        const planned: PlannedExercise[] = (matched.exercises as any[]).map((te: any, index: number) => {
          const sets: WorkoutSet[] = Array.from({ length: te.targetSetsCount }).map((_, sIdx) => ({
            id: `set-${Date.now()}-${index}-${sIdx}`,
            reps: undefined,
            weight: undefined,
            completed: false,
          }));
          return { id: `pe-${Date.now()}-${index}`, exerciseId: te.exerciseId, sets, notes: te.notes };
        });
        setSession({ ...session, plannedExercises: [...session.plannedExercises, ...planned] });
        toast.success(`Loaded template: ${matched.name}`);
      } else {
        toast.error('Template not found');
      }
    }
  };

  const handleSaveAsTemplate = async () => {
    if (session.plannedExercises.length === 0) {
      toast.error('Please add at least one exercise to save as a template');
      return;
    }
    const templateName = prompt('Enter a name for this workout template:', session.name || 'My Routine');
    if (!templateName || !templateName.trim()) {
      if (templateName !== null) toast.error('Template name cannot be empty');
      return;
    }
    const categoryInput = prompt('Enter template category (Push, Pull, Legs, Core/Cardio, Other):', 'Push');
    let templateCategory = 'Other';
    if (categoryInput) {
      const normalized = categoryInput.trim().toLowerCase();
      if (normalized.includes('push')) templateCategory = 'Push';
      else if (normalized.includes('pull')) templateCategory = 'Pull';
      else if (normalized.includes('leg')) templateCategory = 'Legs';
      else if (normalized.includes('core') || normalized.includes('cardio')) templateCategory = 'Core/Cardio';
    }
    const exercisesForTemplate = session.plannedExercises.map((pe) => ({
      exerciseId: pe.exerciseId,
      targetSetsCount: pe.sets.length,
      notes: pe.notes,
    }));
    await saveTemplateMutation.mutateAsync({
      name: templateName.trim(),
      category: templateCategory,
      exercises: exercisesForTemplate,
    });
    toast.success(`Template "${templateName.trim()}" saved under "${templateCategory}"!`);
  };

  const handleMoveWorkout = async () => {
    const newDate = prompt('Enter the date to move this workout to (YYYY-MM-DD):', selectedDate);
    if (!newDate) return;
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(newDate)) {
      toast.error('Please enter a valid date in YYYY-MM-DD format');
      return;
    }
    const existingSessions = await utils.sessions.byDate.fetch({ date: newDate });
    const existingSession = existingSessions?.find((s) => !s.completedAt);
    if (existingSession) {
      if (!confirm(`There is already a planned workout on ${newDate}. Overwrite it?`)) return;
      await trpc.sessions.delete.useMutation().mutateAsync({ sessionId: existingSession.id });
    }
    await saveSessionMutation.mutateAsync({ ...session, date: newDate });
    toast.success(`Workout successfully rescheduled to ${newDate}!`);
    onSave();
  };

  // Auto-populate all working sets from history chips
  const handleAutoPopulateSets = (peId: string, historySets: { reps?: number; weight?: number; durationSeconds?: number }[]) => {
    setSession((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        plannedExercises: prev.plannedExercises.map((pe) => {
          if (pe.id !== peId) return pe;
          const updatedSets = pe.sets.map((set, idx) => {
            if ((set as any).setType === 'warmup' || (set as any).setType === 'kill') return set;
            const historySet = historySets[idx] ?? historySets[historySets.length - 1];
            return {
              ...set,
              weight: historySet?.weight ?? set.weight,
              reps: historySet?.reps ?? set.reps,
              durationSeconds: historySet?.durationSeconds ?? set.durationSeconds,
            };
          });
          return { ...pe, sets: updatedSets };
        }),
      };
    });
    toast.success('Sets populated from last session — beat those numbers!');
  };

  // Check if any working set exceeds the previous session numbers
  const isOverloading = (pe: PlannedExercise, history: { sets: { weight?: number; reps?: number }[] } | null): boolean => {
    if (!history) return false;
    const workingSets = pe.sets.filter((s: any) => s.setType !== 'warmup' && s.setType !== 'kill');
    return workingSets.some((set, idx) => {
      const prev = history.sets[idx] ?? history.sets[history.sets.length - 1];
      if (!prev) return false;
      const weightUp = (set.weight ?? 0) > (prev.weight ?? 0);
      const repsUp = (set.reps ?? 0) > (prev.reps ?? 0) && (set.weight ?? 0) >= (prev.weight ?? 0);
      return weightUp || repsUp;
    });
  };

  // Look up the last completed sets for a given exerciseId across all past sessions
  const getPreviousHistory = (exerciseId: string): { date: string; sets: { reps?: number; weight?: number; durationSeconds?: number }[] } | null => {
    const completedSessions = allSessions
      .filter((s) => s.completedAt && s.date < selectedDate)
      .sort((a, b) => b.date.localeCompare(a.date));
    for (const s of completedSessions) {
      const matchedPE = (s.plannedExercises as any[]).find((pe: any) => pe.exerciseId === exerciseId);
      if (matchedPE) {
        const allSets = (matchedPE.sets as any[]);
        // Accept sets that are individually marked completed, OR any set with weight/duration data
        // from a session that is fully completed (completedAt is set on the session itself).
        // This handles the case where the live tracker didn't persist set.completed=true but the
        // session was still finished.
        const usableSets = allSets.filter((set: any) =>
          (set.weight !== undefined || set.durationSeconds !== undefined) &&
          (set.completed || !!s.completedAt) &&
          (set as any).setType !== 'warmup' && !(set as any).isWarmup
        );
        if (usableSets.length > 0) {
          return {
            date: s.date,
            sets: usableSets.map((set: any) => ({ reps: set.reps, weight: set.weight, durationSeconds: set.durationSeconds })),
          };
        }
      }
    }
    return null;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:min-h-[calc(100vh-180px)]">
      {/* Active Plan / Builder */}
      <div className="lg:col-span-8 space-y-4">
        <div className="forge-panel p-4 space-y-4">
          {/* Planner Header — split day, mesocycle, mode */}
          <div className="space-y-2">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                {plannerData?.splitDayName && plannerData.splitDayName.toLowerCase() !== new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase() ? (
                  <h2 className="text-lg font-black font-mono tracking-wider text-foreground uppercase">
                    {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase()} — {plannerData.splitDayName.toUpperCase()}
                  </h2>
                ) : (
                  <h2 className="text-lg font-black font-mono tracking-wider text-foreground uppercase">
                    {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }).toUpperCase()}
                  </h2>
                )}
                <div className="flex flex-wrap items-center gap-1.5">
                  {(plannerData?.currentWeekNum ?? 0) > 0 && (
                    <ForgePill
                      color={plannerData?.phaseLabel?.toLowerCase().includes('overreach') ? 'orange' : plannerData?.phaseLabel?.toLowerCase().includes('deload') ? 'blue' : plannerData?.phaseLabel?.toLowerCase().includes('maintenance') ? 'purple' : 'green'}
                      label={`W${plannerData?.currentWeekNum} · ${plannerData?.phaseLabel?.toUpperCase() ?? ''}`}
                    />
                  )}
                  {plannerData?.trainingMode && (
                    <ForgePill
                      color={plannerData.trainingMode === 'hypertrophy' ? 'green' : plannerData.trainingMode === 'deload' ? 'blue' : 'yellow'}
                      label={plannerData.trainingMode.toUpperCase()}
                    />
                  )}
                  <ForgePill color="grey" label="DRAFT" />
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {voiceEnabled && (
                  <VoiceMicButton
                    mode="planner"
                    onResult={(result: VoiceResult) => {
                      const parsed = result.parsed as VoiceParsedWorkout;
                      if (!parsed || !parsed.exercises || parsed.exercises.length === 0) {
                        toast.warning('🎙 Couldn\'t parse a workout — try again', { duration: 2500 });
                        return;
                      }
                      setVoicePreview(parsed);
                    }}
                    onError={(msg: string) => toast.error(`🎙 ${msg}`, { duration: 2500 })}
                    size="sm"
                  />
                )}
              </div>
            </div>
          </div>

          {/* Voice Workout Preview Card */}
          {voicePreview && (
            <VoiceWorkoutPreviewCard
              parsed={voicePreview}
              onAccept={(workout: VoiceParsedWorkout) => {
                // Apply the parsed workout to the current session
                const newExercises = workout.exercises.map((ex: VoiceParsedExercise, exIdx: number) => {
                  // Try to match exercise name against library (case-insensitive)
                  const matched = exercises.find(
                    (e) => e.name.toLowerCase() === ex.name.toLowerCase()
                  );
                  const exerciseId = matched?.id ?? `voice-ex-${Date.now()}-${exIdx}`;
                  const sets = ex.sets.flatMap((spec, specIdx) =>
                    Array.from({ length: spec.sets }, (_, si) => ({
                      id: `set-voice-${Date.now()}-${exIdx}-${specIdx}-${si}`,
                      reps: spec.repsMin,
                      weight: undefined,
                      completed: false,
                      setTrackingMode: ex.trackingMode,
                      ...(spec.rpe !== undefined ? { rpe: spec.rpe } : {}),
                      ...(spec.rir !== undefined ? { rir: spec.rir } : {}),
                    } as WorkoutSet))
                  );
                  return {
                    id: `pe-voice-${Date.now()}-${exIdx}`,
                    exerciseId,
                    sets,
                  };
                });
                setSession((prev) => ({
                  ...prev!,
                  name: workout.workoutName || prev!.name,
                  plannedExercises: [...prev!.plannedExercises, ...newExercises],
                }));
                setVoicePreview(null);
                toast.success(`🎙 Added ${newExercises.length} exercise${newExercises.length !== 1 ? 's' : ''} from voice`, { duration: 2000 });
              }}
              onReRecord={() => setVoicePreview(null)}
              onDismiss={() => setVoicePreview(null)}
            />
          )}

          <div className="space-y-3">
            <div>
              <ForgeSectionHeader label="Workout Name" className="mb-1" />
              <Input
                value={session.name}
                onChange={(e) => setSession({ ...session, name: e.target.value })}
                className="bg-secondary border-border text-sm font-semibold text-primary"
                placeholder="e.g. Upper Body Push"
              />
            </div>
            <div>
              <ForgeSectionHeader label="Session Notes" className="mb-1" />
              <Input
                value={session.notes || ''}
                onChange={(e) => setSession({ ...session, notes: e.target.value })}
                className="bg-secondary border-border text-sm"
                placeholder="e.g. Focus on chest connection, take 90s rest"
              />
            </div>
            {/* Tracking Mode Selector — bulk default setter */}
            <div>
              <ForgeSectionHeader label="Workout Default Mode" className="mb-1.5" />
              <div className="grid grid-cols-3 gap-1.5">
                {([
                  { value: 'overload' as TrackingMode, label: 'OVERLOAD', icon: Zap, desc: 'Weight PRs', color: 'text-yellow-400 border-yellow-400/40 bg-yellow-400/5' },
                  { value: 'hypertrophy' as TrackingMode, label: 'HYPERTROPHY', icon: BarChart2, desc: 'Rep/Vol PRs', color: 'text-emerald-400 border-emerald-400/40 bg-emerald-400/5' },
                  { value: 'deload' as TrackingMode, label: 'DELOAD', icon: Battery, desc: 'No PRs', color: 'text-blue-400 border-blue-400/40 bg-blue-400/5' },
                ] as const).map(({ value, label, icon: Icon, desc, color }) => {
                  const isActive = (session.trackingMode ?? 'overload') === value;
                  return (
                    <button
                      key={value}
                      onClick={() => {
                        // Bulk-update all sets that haven't been individually overridden
                        const updated = {
                          ...session,
                          trackingMode: value,
                          plannedExercises: session.plannedExercises.map((pe) => ({
                            ...pe,
                            sets: pe.sets.map((s) => ({ ...s, setTrackingMode: undefined })),
                          })),
                        };
                        setSession(updated);
                      }}
                      style={isActive ? {
                        borderLeft: `3px solid ${value === 'overload' ? '#EAB308' : value === 'hypertrophy' ? '#22C55E' : '#3B82F6'}`,
                        borderTop: '1px solid hsl(var(--border) / 0.25)',
                        borderRight: '1px solid hsl(var(--border) / 0.25)',
                        borderBottom: '1px solid hsl(var(--border) / 0.25)',
                      } : undefined}
                      className={`flex flex-col items-center gap-0.5 p-2 rounded-sm font-mono transition-all bg-white ${
                        isActive
                          ? `${color.replace(/bg-\S+\/\d+/, '').replace(/border-\S+\/\d+/, '').trim()}`
                          : 'border border-border/40 text-muted-foreground hover:border-border hover:text-foreground'
                      }`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      <span className="text-[9px] font-bold tracking-wider">{label}</span>
                      <span className="text-[8px] opacity-70">{desc}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-[9px] font-mono text-muted-foreground mt-1.5 opacity-70">Sets default to this mode — override per set below</p>
            </div>
          </div>

          <div className="space-y-4 pt-2">
            <ForgeSectionHeader label="Planned Movements" className="border-b border-border/30 pb-1" />

            {session.plannedExercises.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-border/50 rounded-sm">
                <Dumbbell className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
                {plannerData?.splitDayCategory ? (
                  <>
                    <p className="text-sm font-mono font-bold text-primary/80 uppercase">{plannerData.splitDayCategory} DAY</p>
                    <p className="text-xs text-muted-foreground font-mono mt-0.5">No exercises added yet — build your session below</p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground font-mono">PLAN IS EMPTY</p>
                )}
                <Button
                  variant="ghost"
                  onClick={() => setShowLibrary(true)}
                  className="mt-2 w-full bg-white border-[1.5px] border-[#1B4332] text-[#1B4332] hover:bg-[#1B4332]/5 font-mono text-xs rounded-lg"
                >
                  BROWSE EXERCISE VAULT
                </Button>
              </div>
            ) : (
              <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
                {(() => {
                  // Build a set of IDs that are the *target* of a superset link
                  // so we can visually group source + target together
                  const supersetTargetIds = new Set(
                    session.plannedExercises
                      .filter(pe => pe.supersetWithId)
                      .map(pe => pe.supersetWithId!)
                  );

                  return session.plannedExercises.map((pe) => {
                  const ex = exercises.find((e) => e.id === pe.exerciseId);
                  let touchStartX = 0;
                  const handleTouchStart = (e: React.TouchEvent) => {
                    touchStartX = e.touches[0].clientX;
                  };
                  const handleTouchEnd = (e: React.TouchEvent, elementId: string) => {
                    const touchEndX = e.changedTouches[0].clientX;
                    const diffX = touchStartX - touchEndX;
                    if (diffX > 100) {
                      const element = document.getElementById(`swipe-ex-${elementId}`);
                      if (element) {
                        element.style.transform = 'translateX(-100%)';
                        element.style.opacity = '0';
                        setTimeout(() => handleRemoveExercise(elementId), 250);
                      }
                    }
                  };

                  // Is this card the SOURCE of a superset link?
                  const isSource = !!pe.supersetWithId;
                  // Is this card the TARGET of a superset link (another card points to it)?
                  const isTarget = supersetTargetIds.has(pe.id);
                  const isSupersetMember = isSource || isTarget;

                  // Connector bridge rendered AFTER the source card (before the target)
                  const pairedEx = isSource
                    ? exercises.find(e => e.id === session.plannedExercises.find(p => p.id === pe.supersetWithId)?.exerciseId)
                    : null;

                  return (
                    <React.Fragment key={pe.id}>
                    <div
                      id={`swipe-ex-${pe.id}`}
                      onTouchStart={handleTouchStart}
                      onTouchEnd={(e) => handleTouchEnd(e, pe.id)}
                      className={`p-3 space-y-2 transition-all duration-250 ease-out transform rounded-sm ${
                        isSupersetMember
                          ? 'border border-orange-500/50 bg-orange-500/5 border-l-[3px] border-l-orange-500'
                          : 'border border-border/80 bg-background/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          {ex ? (
                            <Badge variant="outline" className="font-mono text-[10px] bg-secondary border-primary/30 text-primary">
                              {ex.category.toUpperCase()}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="font-mono text-[10px] bg-secondary border-destructive/30 text-destructive">
                              UNKNOWN
                            </Badge>
                          )}
                          <span className="font-bold text-sm truncate">{ex?.name ?? `Exercise (ID: ${pe.exerciseId.slice(-6)})`}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          {/* Superset link button */}
                          {pe.supersetWithId ? (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleToggleSuperset(pe.id, null)}
                              title="Remove superset link"
                              className="h-7 w-7 text-orange-500 hover:bg-orange-500/10"
                            >
                              <Link2Off className="h-3.5 w-3.5" />
                            </Button>
                          ) : (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => {
                                const others = session.plannedExercises.filter(p => p.id !== pe.id && !p.supersetWithId);
                                if (others.length === 0) {
                                  toast.info('Add another exercise to the plan first, then link them as a superset.');
                                  return;
                                }
                                const names = others.map((p, i) => `${i + 1}. ${exercises.find(e => e.id === p.exerciseId)?.name ?? p.exerciseId}`).join('\n');
                                const sel = prompt(`Link "${ex?.name}" as a superset with:\n\n${names}\n\nEnter number:`);
                                if (!sel) return;
                                const idx = parseInt(sel) - 1;
                                if (isNaN(idx) || idx < 0 || idx >= others.length) { toast.error('Invalid selection'); return; }
                                handleToggleSuperset(pe.id, others[idx]!.id);
                                toast.success(`Superset linked: ${ex?.name} → ${exercises.find(e => e.id === others[idx]!.exerciseId)?.name}`);
                              }}
                              title="Link as superset"
                              className="h-7 w-7 text-muted-foreground hover:text-orange-500 hover:bg-orange-500/10"
                            >
                              <Link className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => ex && handleSwapExercise(pe.id, { ...ex, notes: ex.notes ?? undefined, defaultTimerSeconds: (ex as any).defaultTimerSeconds ?? undefined, specificMuscle: ex.specificMuscle ?? undefined })}
                            title="Swap Exercise"
                            disabled={!ex}
                            className="h-7 w-7 text-primary hover:bg-primary/10"
                          >
                            <RefreshCw className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleRemoveExercise(pe.id)}
                            className="h-7 w-7 text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>

                      {/* Previous workout history bar */}
                      {(() => {
                        const history = getPreviousHistory(pe.exerciseId);
                        if (!history) return null;
                        const ex = exercises.find(e => e.id === pe.exerciseId);
                        const overloading = isOverloading(pe, history);
                        return (
                          <div className={`border rounded-sm p-2 flex items-start justify-between text-xs font-mono transition-colors duration-300 ${
                            overloading
                              ? 'bg-success/5 border-success/30'
                              : 'bg-primary/5 border-primary/20'
                          }`}>
                            <div className="space-y-0.5 min-w-0">
                              <span className="text-[10px] text-muted-foreground uppercase">LAST TIME ({history.date}) — tap to fill</span>
                              <div className="flex flex-wrap gap-1.5">
                                {history.sets.map((set, idx) => (
                                  <button
                                    key={idx}
                                    type="button"
                                    onClick={() => handleAutoPopulateSets(pe.id, history.sets)}
                                    title="Click to populate all sets"
                                    className="bg-secondary px-1.5 py-0.5 rounded border border-border text-[10px] text-primary font-bold hover:bg-primary/15 hover:border-primary/50 active:scale-95 transition-all cursor-pointer"
                                  >
                                    SET {idx + 1}: {ex?.trackingMode === 'time'
                                      ? `${set.durationSeconds}s${set.weight ? ` x ${set.weight} lbs` : ''}`
                                      : `${set.weight} lbs × ${set.reps}`}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div className="text-right text-[10px] uppercase pl-2 border-l border-border/30 shrink-0 flex flex-col items-end gap-0.5">
                              <span className="text-muted-foreground">TARGET</span>
                              {overloading ? (
                                <span className="text-success font-bold flex items-center gap-1">
                                  <TrendingUp className="h-3 w-3" /> OVERLOADING
                                </span>
                              ) : (
                                <span className="text-primary font-bold">OVERLOAD</span>
                              )}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Progression suggestion banner */}
                      {(() => {
                        const prog = plannerData?.progressionMap?.[pe.exerciseId];
                        if (!prog?.suggestion) return null;
                        return (
                          <div className="flex items-start gap-2 px-2 py-1.5 rounded-sm border border-emerald-500/30 bg-emerald-500/5 text-[10px] font-mono">
                            <TrendingUp className="h-3 w-3 text-emerald-400 mt-0.5 shrink-0" />
                            <div className="min-w-0 flex-1">
                              <span className="text-emerald-400 font-bold uppercase tracking-wider">PROGRESSION SUGGESTION</span>
                              <p className="text-emerald-300/80 mt-0.5">{prog.suggestion}</p>
                              {prog.isPlateaued && (
                                <span className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-400 text-[9px] font-bold">
                                  <AlertTriangle className="h-2.5 w-2.5" /> PLATEAU DETECTED
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Superset connector indicator */}
                      {pe.supersetWithId && (() => {
                        const paired = session.plannedExercises.find(p => p.id === pe.supersetWithId);
                        const pairedEx = paired ? exercises.find(e => e.id === paired.exerciseId) : null;
                        return pairedEx ? (
                          <div className="flex items-center gap-1.5 px-2 py-1 mb-1 rounded-sm text-[10px] font-mono" style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.25)' }}>
                            <Link className="h-3 w-3 text-orange-500 shrink-0" />
                            <span className="text-orange-400 uppercase tracking-wider">SUPERSET</span>
                            <span className="text-orange-300/70 mx-1">→</span>
                            <span className="text-orange-300 truncate">{pairedEx.name}</span>
                            <span className="text-orange-500/50 ml-1">NO REST</span>
                          </div>
                        ) : null;
                      })()}

                      <div className="space-y-1.5 pl-2 border-l border-primary/20">
                        {pe.sets.map((set, idx) => {
                          const isChildBlock = !!set.isChildBlock;
                          const isWarmupSet = set.setType === 'warmup' || !!set.isWarmup;
                          const isFeeder = set.setType === 'feeder';
                          const isBackOff = set.setType === 'back_off';
                          const isAmrapSet = set.setType === 'amrap' || !!set.isAmrap;

                          // Working set number (exclude specials)
                          const workingSetNum = (() => {
                            let n = 0;
                            for (let i = 0; i <= idx; i++) {
                              const s = pe.sets[i];
                              if (!s) continue;
                              if (s.isChildBlock || s.setType === 'warmup' || s.isWarmup ||
                                  s.setType === 'feeder' || s.setType === 'back_off') continue;
                              n++;
                            }
                            return n;
                          })();

                          const setLabel = isChildBlock
                            ? (set.childLabel ?? '?')
                            : isWarmupSet ? 'W'
                            : isFeeder ? (set.childLabel ?? 'F')
                            : isBackOff ? 'B'
                            : `SET ${workingSetNum}`;

                          // Type abbreviation
                          const typeAbbrevMap: Record<string, string> = {
                            normal: 'ST', straight: 'ST',
                            overload: 'OL', overload_set: 'OL', kill: 'OL',
                            hypertrophy: 'HY', hyper_set: 'HY',
                            drop: 'DR', drop_set: 'DR',
                            superset: 'SU', giant: 'GI',
                            rest_pause: 'RP',
                            mechanical: 'ME', mechanical_drop: 'ME',
                            cluster: 'CL',
                            amrap: 'AM', failure: 'AM',
                            feeder: 'FE', warmup: 'FE',
                            back_off: 'BO',
                          };
                          const abbrev = typeAbbrevMap[set.setType ?? 'straight'] ?? 'ST';
                          const typeCfg = getSetTypeBadgeConfig(set.setType as any, set.isWarmup);

                          // Row border style
                          const st = set.setType as string | undefined;
                          const rowStyle = isWarmupSet
                            ? 'border-l-[3px] border-l-success border-success/25 bg-success/5'
                            : st === 'drop' || (isChildBlock && st === 'drop')
                            ? 'border-l-[3px] border-l-amber-500 border-amber-500/25 bg-amber-500/5'
                            : st === 'cluster' || (isChildBlock && st === 'cluster')
                            ? 'border-l-[3px] border-l-cyan-500 border-cyan-500/25 bg-cyan-500/5'
                            : st === 'mechanical' || (isChildBlock && st === 'mechanical')
                            ? 'border-l-[3px] border-l-purple-500 border-purple-500/25 bg-purple-500/5'
                            : isFeeder
                            ? 'border-l-[3px] border-l-slate-400 border-slate-400/25 bg-slate-400/5'
                            : isBackOff
                            ? 'border-l-[3px] border-l-teal-500 border-teal-500/25 bg-teal-500/5'
                            : set.setType === 'overload_set'
                            ? 'border-l-[3px] border-l-red-500 border-red-500/25 bg-red-500/5'
                            : set.setType === 'hyper_set'
                            ? 'border-l-[3px] border-l-emerald-500 border-emerald-500/25 bg-emerald-500/5'
                            : 'bg-background/40 border-border';

                          return (
                            <div key={set.id} className={isChildBlock ? 'ml-3 border-l-2 border-dashed border-border/40 pl-2' : ''}>
                              <div className={`rounded-sm border px-2 pt-2 pb-1.5 ${isChildBlock ? 'border-transparent' : ''} ${rowStyle}`}>

                                {/* LINE 1: label · type badge · mode toggle · [revert] · trash */}
                                <div className="flex items-center gap-2 mb-1.5">
                                  <span className={`font-mono text-[11px] font-bold shrink-0 ${
                                    isChildBlock
                                      ? set.setType === 'drop' ? 'text-amber-400'
                                        : set.setType === 'cluster' ? 'text-cyan-400'
                                        : set.setType === 'mechanical' ? 'text-purple-400'
                                        : 'text-muted-foreground'
                                      : 'text-foreground'
                                  }`}>{setLabel}</span>

                                  {/* Type pill — tappable for non-child rows */}
                                  {!isChildBlock ? (
                                    <button
                                      onClick={() => setSetTypePicker(
                                        setTypePicker?.peId === pe.id && setTypePicker?.setId === set.id
                                          ? null
                                          : { peId: pe.id, setId: set.id }
                                      )}
                                      title="Set type — tap to change"
                                      className={`px-1.5 py-0.5 rounded-sm border font-mono text-[9px] font-bold leading-none transition-all shrink-0 hover:opacity-100 active:scale-95 ${
                                        typeCfg ? `${typeCfg.colorClass} ${typeCfg.bgClass} ${typeCfg.borderClass}` : 'text-slate-400 border-slate-500/30 bg-slate-500/10'
                                      }`}
                                    >
                                      {abbrev}
                                    </button>
                                  ) : (
                                    <span className={`px-1.5 py-0.5 rounded-sm border font-mono text-[9px] font-bold leading-none shrink-0 ${
                                      typeCfg ? `${typeCfg.colorClass} ${typeCfg.bgClass} ${typeCfg.borderClass}` : 'text-muted-foreground border-border/30'
                                    }`}>{abbrev}</span>
                                  )}

                                  {/* Per-set tracking mode toggle */}
                                  {(() => {
                                    const effectiveMode: TrackingMode = set.setTrackingMode ?? (session.trackingMode ?? 'overload');
                                    const isOverridden = !!set.setTrackingMode;
                                    const cycleMode = () => {
                                      const next: TrackingMode = effectiveMode === 'overload' ? 'hypertrophy' : effectiveMode === 'hypertrophy' ? 'deload' : 'overload';
                                      handleSetChange(pe.id, set.id, 'setTrackingMode', next);
                                    };
                                    const modeConfig = {
                                      overload: { label: 'O', color: 'text-yellow-400 border-yellow-400/40 bg-yellow-400/5', title: 'Overload' },
                                      hypertrophy: { label: 'H', color: 'text-emerald-400 border-emerald-400/40 bg-emerald-400/5', title: 'Hypertrophy' },
                                      deload: { label: 'D', color: 'text-blue-400 border-blue-400/40 bg-blue-400/5', title: 'Deload' },
                                    }[effectiveMode];
                                    return (
                                      <button
                                        onClick={cycleMode}
                                        title={`${modeConfig.title}${isOverridden ? ' (overridden)' : ' (default)'} — tap to cycle`}
                                        className={`px-1.5 py-0.5 rounded-sm border font-mono text-[9px] font-bold leading-none shrink-0 transition-all ${modeConfig.color} ${isOverridden ? 'ring-1 ring-current' : 'opacity-50 hover:opacity-100'}`}
                                      >
                                        {modeConfig.label}
                                      </button>
                                    );
                                  })()}

                                  <div className="flex-1" />

                                  {/* Revert to Straight (parent multi-block only) */}
                                  {!isChildBlock && ['drop', 'cluster', 'mechanical'].includes(set.setType ?? '') && (
                                    <button
                                      onClick={() => handleRevertSetToStraight(pe.id, set.id)}
                                      title="Revert to Straight set"
                                      className="text-muted-foreground hover:text-amber-500 transition-colors p-1"
                                    >
                                      <RotateCcw className="h-3 w-3" />
                                    </button>
                                  )}

                                  {/* Trash */}
                                  {pe.sets.length > 1 && (
                                    <button
                                      onClick={() => handleRemoveSet(pe.id, set.id)}
                                      className="text-muted-foreground hover:text-destructive transition-colors p-1"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  )}
                                </div>

                                {/* LINE 2: inputs */}
                                <div className="flex items-center gap-2">
                                  {ex?.trackingMode === 'time' ? (
                                    <>
                                      <Input
                                        type="number"
                                        placeholder="Secs"
                                        value={set.durationSeconds ?? ''}
                                        onChange={(e) => handleSetChange(pe.id, set.id, 'durationSeconds', e.target.value ? parseInt(e.target.value) : undefined)}
                                        className="h-9 flex-1 text-center font-mono bg-secondary border-border"
                                        inputMode="numeric"
                                      />
                                      <span className="text-muted-foreground font-mono text-xs shrink-0">×</span>
                                      <Input
                                        type="number"
                                        placeholder="LBS"
                                        value={set.weight ?? ''}
                                        onChange={(e) => handleSetChange(pe.id, set.id, 'weight', e.target.value ? parseFloat(e.target.value) : undefined)}
                                        className="h-9 flex-1 text-center font-mono bg-secondary border-border"
                                        inputMode="decimal"
                                      />
                                      <span className="text-muted-foreground font-mono text-[10px] shrink-0">LBS</span>
                                      <PlateSelector currentWeight={set.weight} onChange={(val) => handleSetChange(pe.id, set.id, 'weight', val)} size="xs" />
                                    </>
                                  ) : (
                                    <>
                                      <Input
                                        type="number"
                                        placeholder={isAmrapSet ? '∞' : 'REPS'}
                                        value={set.reps ?? ''}
                                        onChange={(e) => handleSetChange(pe.id, set.id, 'reps', e.target.value ? parseInt(e.target.value) : undefined)}
                                        className={`h-9 flex-1 text-center font-mono bg-secondary border-border ${
                                          isAmrapSet ? 'border-blue-500/50 bg-blue-500/5' : ''
                                        }`}
                                        inputMode="numeric"
                                      />
                                      <span className="text-muted-foreground font-mono text-xs shrink-0">×</span>
                                      <Input
                                        type="number"
                                        placeholder="LBS"
                                        value={set.weight ?? ''}
                                        onChange={(e) => handleSetChange(pe.id, set.id, 'weight', e.target.value ? parseFloat(e.target.value) : undefined)}
                                        className="h-9 flex-1 text-center font-mono bg-secondary border-border"
                                        inputMode="decimal"
                                      />
                                      <span className="text-muted-foreground font-mono text-[10px] shrink-0">LBS</span>
                                      <PlateSelector currentWeight={set.weight} onChange={(val) => handleSetChange(pe.id, set.id, 'weight', val)} size="xs" />
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        {/* Add-set palette */}
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          <button
                            onClick={() => handleAddSet(pe.id, 'straight')}
                            className="px-2 py-1 rounded-sm border font-mono text-[9px] font-bold text-slate-300 border-slate-500/30 bg-slate-500/10 hover:opacity-100 active:scale-95 transition-all"
                          >+ STRAIGHT</button>
                          <button
                            onClick={() => handleAddSet(pe.id, 'overload_set')}
                            className="px-2 py-1 rounded-sm border font-mono text-[9px] font-bold text-red-400 border-red-500/30 bg-red-500/10 hover:opacity-100 active:scale-95 transition-all"
                          >+ OVERLOAD</button>
                          <button
                            onClick={() => handleAddSet(pe.id, 'hyper_set')}
                            className="px-2 py-1 rounded-sm border font-mono text-[9px] font-bold text-emerald-400 border-emerald-500/30 bg-emerald-500/10 hover:opacity-100 active:scale-95 transition-all"
                          >+ HYPER</button>
                          <button
                            onClick={() => handleAddSet(pe.id, 'drop')}
                            className="px-2 py-1 rounded-sm border font-mono text-[9px] font-bold text-amber-400 border-amber-500/30 bg-amber-500/10 hover:opacity-100 active:scale-95 transition-all"
                          >+ DROP</button>
                          <button
                            onClick={() => handleAddSet(pe.id, 'cluster')}
                            className="px-2 py-1 rounded-sm border font-mono text-[9px] font-bold text-cyan-400 border-cyan-500/30 bg-cyan-500/10 hover:opacity-100 active:scale-95 transition-all"
                          >+ CLUSTER</button>
                          <button
                            onClick={() => handleAddSet(pe.id, 'feeder')}
                            className="px-2 py-1 rounded-sm border font-mono text-[9px] font-bold text-slate-400 border-slate-400/30 bg-slate-400/10 hover:opacity-100 active:scale-95 transition-all"
                          >+ FEEDER</button>
                          <button
                            onClick={() => handleAddSet(pe.id, 'back_off')}
                            className="px-2 py-1 rounded-sm border font-mono text-[9px] font-bold text-teal-400 border-teal-500/30 bg-teal-500/10 hover:opacity-100 active:scale-95 transition-all"
                          >+ BACK-OFF</button>
                          <button
                            onClick={() => handleGenerateWarmupSets(pe.id)}
                            className="px-2 py-1 rounded-sm border font-mono text-[9px] font-bold text-success border-success/30 bg-success/10 hover:opacity-100 active:scale-95 transition-all"
                          >+ WARM-UP</button>
                        </div>
                      </div>
                    </div>

                    {/* Connector bridge between source and target cards */}
                    {isSource && pairedEx && (
                      <div className="flex items-center gap-2 px-3 py-1.5" style={{ marginTop: '-4px', marginBottom: '-4px' }}>
                        <div className="w-[3px] self-stretch bg-orange-500/60 rounded-full ml-[2px]" style={{ minHeight: '28px' }} />
                        <div className="flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10px] font-mono" style={{ background: 'rgba(249,115,22,0.1)', border: '1px dashed rgba(249,115,22,0.4)' }}>
                          <Link className="h-3 w-3 text-orange-500" />
                          <span className="text-orange-400 uppercase tracking-wider">NO REST</span>
                          <span className="text-orange-300/60 mx-0.5">·</span>
                          <span className="text-orange-300/80">GO STRAIGHT TO</span>
                          <span className="text-orange-400 font-bold ml-0.5">{pairedEx.name.toUpperCase()}</span>
                        </div>
                      </div>
                    )}
                    </React.Fragment>
                  );
                  })}
                )()}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-4 border-t border-border/50 justify-between items-center">
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={handleLoadTemplate}
                className="font-mono text-xs border-muted-foreground/30 hover:bg-secondary"
              >
                LOAD TEMPLATE
              </Button>
              <Button
                variant="outline"
                onClick={handleSaveAsTemplate}
                className="font-mono text-xs border-primary/40 text-primary hover:bg-primary/10"
              >
                SAVE AS TEMPLATE
              </Button>
              {session.plannedExercises.length > 0 && (
                <Button
                  variant="outline"
                  onClick={handleMoveWorkout}
                  className="font-mono text-xs border-border text-muted-foreground hover:text-primary hover:border-primary/40"
                >
                  MOVE WORKOUT
                </Button>
              )}
            </div>
            <div className="flex gap-2 ml-auto">
              <Button
                variant="outline"
                onClick={handleSave}
                disabled={saveSessionMutation.isPending}
                className="font-mono text-xs border-primary text-primary hover:bg-primary/10"
              >
                <Save className="h-4 w-4 mr-1" /> SAVE PLAN
              </Button>
              <Button
                onClick={async () => {
                  if (session.plannedExercises.length === 0) {
                    toast.error('Please add at least one exercise to start a live session');
                    return;
                  }
                  await saveSessionMutation.mutateAsync(session);
                  onStartLive(session);
                }}
                disabled={saveSessionMutation.isPending}
                className="bg-success text-success-foreground hover:bg-success/90 font-mono text-xs"
              >
                <Play className="h-4 w-4 mr-1" /> START LIVE SESSION
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Side Library View (Desktop) */}
      <div className="hidden lg:block lg:col-span-4">
        <div className="forge-panel p-4">
          <div className="mb-2 px-1 flex items-center justify-between">
            <ForgeSectionHeader label="Exercise Vault" />
            {splitCategory && (
              <ForgePill color="legs" label={`${splitCategory} TARGETS`} size="xs" />
            )}
          </div>
          <ExerciseLibrary
            onSelectExercise={handleAddExercise}
            onRemoveExercise={(exerciseId) => {
              const pe = session.plannedExercises.find(p => p.exerciseId === exerciseId);
              if (pe) handleRemoveExercise(pe.id);
            }}
            showSelectButton={true}
            defaultCategory={splitCategory}
            addedExerciseIds={session.plannedExercises.map(pe => pe.exerciseId)}
          />
        </div>
      </div>

      {/* Mobile Floating Add Button */}
      <div className="lg:hidden fixed bottom-16 right-4 z-40">
        <Button
          onClick={() => setShowLibrary(true)}
          className="h-12 w-12 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center border border-primary-foreground/20"
        >
          <Plus className="h-6 w-6" />
        </Button>
      </div>

      {/* Swap Exercise Modal */}
      {swapModal && (
        <div className="lg:hidden fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex flex-col justify-end">
          <div className="bg-card border-t border-border rounded-t-lg max-h-[85vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <span className="font-mono text-sm font-bold text-primary">SWAP EXERCISE</span>
                <p className="text-[10px] font-mono text-muted-foreground mt-0.5">Replacing: {swapModal.currentExercise.name}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSwapModal(null)} className="font-mono text-xs">CLOSE</Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 pb-12">
              <ExerciseLibrary
                onSelectExercise={(ex) => {
                  setSession(prev => {
                    if (!prev) return prev;
                    return {
                      ...prev,
                      plannedExercises: prev.plannedExercises.map(pe =>
                        pe.id === swapModal.peId
                          ? { ...pe, exerciseId: ex.id }
                          : pe
                      )
                    };
                  });
                  toast.success(`Swapped to ${ex.name}`);
                  setSwapModal(null);
                }}
                showSelectButton={true}
                defaultCategory={swapModal.currentExercise.category ?? undefined}
              />
            </div>
          </div>
        </div>
      )}

      {/* Mobile Exercise Library Sheet */}
      {showLibrary && (
        <div className="lg:hidden fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex flex-col justify-end">
          <div className="bg-card border-t border-border rounded-t-lg max-h-[85vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <span className="font-mono text-sm font-bold text-primary">SELECT EXERCISE</span>
              <Button variant="ghost" size="sm" onClick={() => setShowLibrary(false)} className="font-mono text-xs">
                CLOSE
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 pb-12">
              {splitCategory && (
                <div className="mb-3 px-1 flex items-center justify-between">
                  <ForgeSectionHeader label="Vault Auto-Filter" />
                  <ForgePill color="legs" label={`${splitCategory} TARGETS`} size="xs" />
                </div>
              )}
              <ExerciseLibrary
                onSelectExercise={(ex) => {
                  handleAddExercise(ex);
                  setShowLibrary(false);
                }}
                onRemoveExercise={(exerciseId) => {
                  const pe = session.plannedExercises.find(p => p.exerciseId === exerciseId);
                  if (pe) handleRemoveExercise(pe.id);
                }}
                showSelectButton={true}
                defaultCategory={splitCategory}
                addedExerciseIds={session.plannedExercises.map(pe => pe.exerciseId)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
